import { isIE11 } from "@montana/helpers/browser.helper";

type Callback = (entry: IntersectionObserverEntry) => void;

interface IObserverInstance {
    observed: IObserved[];
    observer: IntersectionObserver;
}

interface IObserved {
    callback: Callback;
    element: Element;
}

class InView {
    private instances: Map<string, IObserverInstance>;

    public constructor() {
        this.instances = new Map();
    }

    public observe = (key: string, element: Element, callback: Callback, options: IntersectionObserverInit = {}): void => {
        const instance = this.instances.get(key)
            || {
                observed: [],
                observer: new IntersectionObserver(this.onChange, options),
            };

        instance.observed.push({ callback, element });

        this.instances.set(key, instance);
        instance.observer.observe(element);
    };

    public unobserve = (key: string, element: Element): void => {
        const instance = this.instances.get(key);
        if (!instance) { return; }

        const unobservedInstance = instance.observed.find((i): boolean => i.element === element);
        if (unobservedInstance) {
            instance.observer.unobserve(unobservedInstance.element);
        }

        const otherInstances = instance.observed.filter((i): boolean => i.element !== element);
        if (otherInstances.length > 0) {
            instance.observed = otherInstances;
        } else {
            instance.observer.disconnect();
            this.instances.delete(key);
        }
    };

    private onChange = (entries: IntersectionObserverEntry[]): void => {

        // IE11 doesn't like Map.values(), and it's difficult to polyfill, so for that browser we'll
        // jump through some hoops. Everything else just gets the cool version below.
        const dataSource = isIE11
            ? ((): IObserverInstance[] => {
                const usableInstances: IObserverInstance[] = [];
                this.instances.forEach((currentInstance): number => usableInstances.push(currentInstance));
                return usableInstances;
            })()
            : Array.from(this.instances.values());

        entries.forEach((entry): void => {

            const instance = dataSource
                .map((i): IObserved[] => i.observed)
                .reduce((acc, v): IObserved[] => acc.concat(v), [])
                .filter((subInstance): boolean => subInstance.element === entry.target);

            instance.forEach((i): void => i.callback(entry));
        });
    }
}

export default new InView();
